import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, Tabs, Tab
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchableSelect from '../../components/SearchableSelect';
import { useSnackbar } from 'notistack';
import articuloXListaPrecioService, { ARTICULO_X_LISTA_PRECIO_DESCRIPCION_MAX_LENGTH } from '../../services/articuloXListaPrecioService';
import ListaPrecioSearch from '../../components/ListaPrecioSearch';

interface ArticuloXListaPrecioFormData {
  codigoLista: string;
  codigoArticulo: string;
  descripcion: string;
  codigoMoneda?: string;
  precioVta?: number;
  precioCosto?: number;
  propio: string;
  iva: string;
  descuento?: string;
  flete?: number;
  fleteFp: string;
  porcentaje?: number;
  codigoRamo: string;
  nombreRamo: string;
  codigoLinea: string;
  nombreLinea: string;
  codigoClase: string;
  nombreClase?: string;
  variacion?: number;
  variacionFp?: string;
  variacionBasadaEn?: string;
  variacionSigno?: string;
}

const defaultForm: ArticuloXListaPrecioFormData = {
  codigoLista: '',
  codigoArticulo: '',
  descripcion: '',
  propio: 'N',
  iva: 'S',
  fleteFp: 'N',
  codigoRamo: '',
  nombreRamo: '',
  codigoLinea: '',
  nombreLinea: '',
  codigoClase: '',
  precioVta: 0,
  precioCosto: 0
};

const ArticuloXListaPrecioForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nroLista, codigoArticulo } = useParams();
  const [formData, setFormData] = useState<ArticuloXListaPrecioFormData>({
    ...defaultForm,
    codigoLista: nroLista && nroLista !== 'nuevo' ? nroLista : '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [listaSearchOpen, setListaSearchOpen] = useState(false);

  const loadArticulo = useCallback(async () => {
    if (!nroLista || !codigoArticulo || nroLista === 'nuevo' || codigoArticulo === 'nuevo') return;
    try {
      setLoading(true);
      const articulo = await articuloXListaPrecioService.getArticuloXListaPrecio(nroLista, codigoArticulo);
      setFormData(articulo);
    } catch (error) {
      enqueueSnackbar('Error al cargar el artículo', { variant: 'error' });
      navigate('/ventas/articuloxlistaprecio');
    } finally {
      setLoading(false);
    }
  }, [nroLista, codigoArticulo, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (nroLista && nroLista !== 'nuevo' && codigoArticulo && codigoArticulo !== 'nuevo') loadArticulo();
  }, [nroLista, codigoArticulo, loadArticulo]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericFields = ['precioVta', 'precioCosto', 'flete', 'porcentaje', 'variacion'];
    
    if (numericFields.includes(name)) {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleListaSelect = (lista: any) => {
    setFormData(prev => ({
      ...prev,
      codigoLista: lista.codigoLista
    }));
    setListaSearchOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.codigoLista.trim() || !formData.codigoArticulo.trim()) {
        enqueueSnackbar('Complete los campos obligatorios', { variant: 'warning' });
        return;
      }

      if (formData.precioVta && formData.precioVta <= 0) {
        enqueueSnackbar('El precio de venta debe ser mayor a 0', { variant: 'warning' });
        return;
      }

      if (nroLista && nroLista !== 'nuevo' && codigoArticulo && codigoArticulo !== 'nuevo') {
        await articuloXListaPrecioService.updateArticuloXListaPrecio(nroLista, codigoArticulo, formData);
      } else {
        await articuloXListaPrecioService.createArticuloXListaPrecio(formData);
      }

      enqueueSnackbar('Artículo guardado exitosamente', { variant: 'success' });
      navigate(-1);
    } catch (error: any) {
      console.error('Error al guardar artículo:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar el artículo', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/ventas/articuloxlistaprecio')}
              sx={{ textTransform: 'none' }}
            >
              Artículos por Lista de Precio
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {nroLista && nroLista !== 'nuevo' && codigoArticulo && codigoArticulo !== 'nuevo' ? 'Editar Artículo' : 'Nuevo Artículo'}
            </Typography>
          </Stack>

          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
            <Tab label="General" />
            <Tab label="Precios" />
            <Tab label="Variaciones" />
            <Tab label="Clasificación" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1}>
                    <TextField 
                      label="Lista de Precio" 
                      value={formData.codigoLista || ''} 
                      fullWidth 
                      required
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button 
                      variant="outlined" 
                      onClick={() => setListaSearchOpen(true)}
                      disabled={!!(nroLista && nroLista !== 'nuevo' && codigoArticulo && codigoArticulo !== 'nuevo')}
                      sx={{ minWidth: 120 }}
                    >
                      Buscar
                    </Button>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="codigoArticulo" 
                    label="Código Artículo" 
                    value={formData.codigoArticulo} 
                    onChange={handleChange} 
                    fullWidth 
                    required
                    disabled={!!(nroLista && nroLista !== 'nuevo' && codigoArticulo && codigoArticulo !== 'nuevo')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    name="descripcion" 
                    label="Descripción" 
                    value={formData.descripcion} 
                    onChange={handleChange} 
                    fullWidth 
                    required
                    inputProps={{ maxLength: ARTICULO_X_LISTA_PRECIO_DESCRIPCION_MAX_LENGTH }}
                    helperText={`Máximo ${ARTICULO_X_LISTA_PRECIO_DESCRIPCION_MAX_LENGTH} caracteres en el ítem de la lista.`}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="precioVta" 
                    label="Precio Venta" 
                    type="number"
                    value={formData.precioVta || ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ step: '0.01', min: '0' }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="precioCosto" 
                    label="Precio Costo" 
                    type="number"
                    value={formData.precioCosto || ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ step: '0.01', min: '0' }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SearchableSelect
                    name="iva"
                    label="IVA"
                    value={formData.iva}
                    onChange={(v) => setFormData(prev => ({ ...prev, iva: v ?? 'N' }))}
                    options={[
                      { value: 'S', label: 'Sí' },
                      { value: 'N', label: 'No' },
                    ]}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="porcentaje" 
                    label="Porcentaje" 
                    type="number"
                    value={formData.porcentaje || ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ step: '0.01', min: '0' }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <SearchableSelect
                    name="variacionFp"
                    label="Variación Forma de Pago"
                    value={formData.variacionFp || 'N'}
                    onChange={(v) => setFormData(prev => ({ ...prev, variacionFp: v ?? 'N' }))}
                    options={[
                      { value: 'S', label: 'Sí' },
                      { value: 'N', label: 'No' },
                    ]}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="variacion" 
                    label="Variación" 
                    type="number"
                    value={formData.variacion || ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ step: '0.01' }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 3 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="codigoRamo" 
                    label="Código Ramo" 
                    value={formData.codigoRamo} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField 
                    name="nombreRamo" 
                    label="Nombre Ramo" 
                    value={formData.nombreRamo} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="codigoLinea" 
                    label="Código Línea" 
                    value={formData.codigoLinea} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField 
                    name="nombreLinea" 
                    label="Nombre Línea" 
                    value={formData.nombreLinea} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="codigoClase" 
                    label="Código Clase" 
                    value={formData.codigoClase} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField 
                    name="nombreClase" 
                    label="Nombre Clase" 
                    value={formData.nombreClase || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate(-1)} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <ListaPrecioSearch
        open={listaSearchOpen}
        onClose={() => setListaSearchOpen(false)}
        onSelect={handleListaSelect}
      />
    </Box>
  );
};

export default ArticuloXListaPrecioForm;
